/**
 * CVD (Cumulative Volume Delta) Indicator
 *
 * Renders CVD as a histogram in a separate pane below the main chart.
 * CVD shows the running sum of (buy volume - sell volume) and helps
 * identify order flow direction and divergences with price.
 */

import { CandlestickSeries, LineSeries } from 'lightweight-charts';

export class CVDIndicator {
  constructor(chart, mainSeries) {
    this.chart = chart;
    this.mainSeries = mainSeries;
    this.cvdSeries = null;
    this.zeroLine = null;
    this.enabled = false;
    this._marginCallback = null;
  }

  /**
   * Enable the CVD indicator
   * @param {Array} candles - Array of candles with cvd field
   */
  enable(candles) {
    if (this.enabled) {
      this.update(candles);
      return;
    }

    // Create CVD candlestick series using the left price scale
    this.cvdSeries = this.chart.addSeries(CandlestickSeries, {
      priceScaleId: 'left',  // Use built-in left price scale
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderUpColor: '#26a69a',
      borderDownColor: '#ef5350',
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
      priceFormat: {
        type: 'volume',
      },
      lastValueVisible: true,
      priceLineVisible: false,
    });

    // Create zero line for CVD reference
    this.zeroLine = this.chart.addSeries(LineSeries, {
      priceScaleId: 'left',  // Same price scale as CVD
      color: '#666666',
      lineWidth: 1,
      lineStyle: 2,  // Dashed line
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
    });

    // Configure the left price scale for CVD (separate pane at bottom)
    this.chart.priceScale('left').applyOptions({
      visible: true,
      borderVisible: true,
      borderColor: '#2a2a2a',
      autoScale: true,
    });

    this.enabled = true;
    this._marginCallback?.();

    this.update(candles);

    console.log('[CVDIndicator] Enabled');
  }

  /**
   * Get the Globex session date for a given timestamp.
   * Globex opens at 6 PM ET (23:00 UTC in EST, 22:00 UTC in EDT).
   * Returns the trading date (the day the session belongs to).
   */
  getSessionDate(timestamp) {
    const date = new Date(timestamp * 1000);
    // Convert to ET by checking the hour in ET timezone
    const etTime = date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      hour12: false
    });
    const etHour = parseInt(etTime, 10);

    // Get the date in ET
    const etDateStr = date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    // If hour >= 18 (6 PM), this belongs to the next day's session
    if (etHour >= 18) {
      const nextDay = new Date(date.getTime() + 24 * 60 * 60 * 1000);
      return nextDay.toLocaleString('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    }

    return etDateStr;
  }

  /**
   * Update CVD data as candlesticks
   * @param {Array} candles - Array of candles with delta or buyVolume/sellVolume fields
   */
  update(candles) {
    if (!this.cvdSeries || !candles || candles.length === 0) {
      return;
    }

    // Calculate CVD candlesticks client-side
    // Each CVD candle shows: open (prev CVD), high, low, close (current CVD)
    // Reset CVD at each Globex session boundary (6 PM ET)
    const cvdData = [];
    let cvd = 0;
    let currentSession = null;

    for (const candle of candles) {
      // Calculate delta from buyVolume/sellVolume or delta field
      // Try multiple field name formats (camelCase and snake_case).
      // sell_volume left the wire (API alignment 3.4): derive it as
      // volume - buy_volume (exact identity in the stored candles); wires
      // that still carry sell_volume short-circuit unchanged.
      const buyVol = candle.buyVolume || candle.buy_volume || 0;
      const sellVol = candle.sellVolume || candle.sell_volume ||
        ((candle.volume || 0) - buyVol);

      let delta;
      if (buyVol > 0 || sellVol > 0) {
        // Have buy/sell breakdown - use it directly (backend side labels are
        // correct as of the 2026-06 side-inversion fix; no compensation needed)
        delta = buyVol - sellVol;
      } else if (candle.delta !== undefined && candle.delta !== null) {
        // Use pre-calculated delta directly
        delta = candle.delta;
      } else {
        // No volume data available - skip this candle for CVD
        delta = 0;
      }

      // Check for session boundary and reset CVD
      const sessionDate = this.getSessionDate(candle.time);
      if (sessionDate !== currentSession) {
        cvd = 0;  // Reset CVD at session boundary
        currentSession = sessionDate;
      }

      // CVD candle OHLC:
      // Open = CVD before this candle's delta
      // Close = CVD after adding this candle's delta
      // High/Low = max/min of open and close (since we only have the final delta)
      const cvdOpen = cvd;
      cvd += delta;
      const cvdClose = cvd;

      cvdData.push({
        time: candle.time,
        open: cvdOpen,
        high: Math.max(cvdOpen, cvdClose),
        low: Math.min(cvdOpen, cvdClose),
        close: cvdClose,
      });
    }

    this.cvdSeries.setData(cvdData);

    // Store last CVD value for real-time updates
    this.lastCvd = cvd;
    this.baseCvd = cvd;  // Base CVD for in-progress candle updates

    // Update zero line to span the entire time range
    if (this.zeroLine && cvdData.length > 0) {
      const zeroLineData = [
        { time: cvdData[0].time, value: 0 },
        { time: cvdData[cvdData.length - 1].time, value: 0 },
      ];
      this.zeroLine.setData(zeroLineData);
    }

    console.log('[CVDIndicator] Updated with', cvdData.length, 'CVD candles, final CVD:', cvd.toFixed(0));
  }

  /**
   * Disable the CVD indicator
   */
  disable() {
    if (!this.enabled) {
      return;
    }

    if (this.cvdSeries) {
      this.chart.removeSeries(this.cvdSeries);
      this.cvdSeries = null;
    }

    if (this.zeroLine) {
      this.chart.removeSeries(this.zeroLine);
      this.zeroLine = null;
    }

    this.enabled = false;
    this._marginCallback?.();

    console.log('[CVDIndicator] Disabled');
  }

  /**
   * Update the last CVD candle with new data (for real-time updates)
   * @param {Object} candle - The updated candle data
   * @param {number} prevCvd - The CVD value before this candle
   */
  updateLastCandle(candle, prevCvd = null, isComplete = false) {
    if (!this.cvdSeries || !this.enabled) {
      return;
    }

    // Calculate delta for this candle (sell_volume derived when off the
    // wire, same as update() above)
    const buyVol = candle.buyVolume || candle.buy_volume || 0;
    const sellVol = candle.sellVolume || candle.sell_volume ||
      ((candle.volume || 0) - buyVol);

    let delta;
    if (buyVol > 0 || sellVol > 0) {
      delta = buyVol - sellVol;
    } else if (candle.delta !== undefined && candle.delta !== null) {
      delta = candle.delta;
    } else {
      delta = 0;
    }

    // For in-progress candles, use the base CVD (end of last completed candle)
    // For completed candles, use lastCvd and then update it
    const cvdOpen = prevCvd !== null ? prevCvd : (this.baseCvd ?? this.lastCvd ?? 0);
    const cvdClose = cvdOpen + delta;

    // Update the series with the new candle
    this.cvdSeries.update({
      time: candle.time,
      open: cvdOpen,
      high: Math.max(cvdOpen, cvdClose),
      low: Math.min(cvdOpen, cvdClose),
      close: cvdClose,
    });

    // Only update lastCvd when candle completes
    // This prevents cumulative drift during in-progress updates
    if (isComplete) {
      this.lastCvd = cvdClose;
      this.baseCvd = cvdClose;  // Update base for next candle
    }
  }

  /**
   * Set the margin coordination callback
   * @param {Function} fn - Callback to invoke when margins need updating
   */
  setMarginCallback(fn) {
    this._marginCallback = fn;
  }

  /**
   * Check if indicator is enabled
   */
  isEnabled() {
    return this.enabled;
  }
}

export default CVDIndicator;
